import React, { ComponentProps } from "react";
import {
  generateTestId,
  TestID,
  TestIDWrapper,
} from "../../shared/testids/testids";
import { Cascader, CascaderProps, Col, Row } from "antd";
import "./CORECascader.less";
import { onPreventMouseDown } from "./CORESelect";
import { DefaultOptionType } from "rc-cascader";
import { WidthSize } from "./COREInput";
import classNames from "classnames";
import { COREIcon } from "../Content/COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { spacing } from "../../shared/global";
import { CORETag } from "../Content/CORETag";
import { CORETooltip } from "../Overlay/CORETooltip";
import { SingleValueType } from "rc-cascader/lib/Cascader";

type Size = "sm" | "md" | "lg";
type DropdownSize = "sm" | "lg";

export declare type ValueType = SingleValueType & SingleValueType[];

export type CORECascaderProps = Omit<
  CascaderProps<DefaultOptionType>,
  "size" | "maxTagCount"
> & {
  testID: TestID;
  size?: Size;
  dropdownSize?: DropdownSize;
  widthSize?: WidthSize;
  value?: ValueType;
};

export type CascaderOption = {
  value: string | number;
  label: string;
  children?: CascaderOption[];
  disabled?: boolean;
};

const CORESizeToAntSize: Record<Size, ComponentProps<typeof Cascader>["size"]> =
  {
    sm: "small",
    md: "middle",
    lg: "large",
  };

export const CORECascader: React.FC<CORECascaderProps> = ({
  size = "md",
  testID,
  widthSize = "md",
  dropdownSize = "sm",
  suffixIcon,
  tagRender = (tagProps) => (
    <CORETag
      type={"basic"}
      label={tagProps.label}
      closable={tagProps.closable}
      onClose={tagProps.onClose}
      onMouseDown={onPreventMouseDown}
    />
  ),
  onChange,
  value,
  multiple,
  options,
  ...props
}) => {
  return (
    <TestIDWrapper testID={testID} className={"core-cascader"}>
      <div className={`input-width-${widthSize}`}>
        <Cascader
          popupClassName={classNames(
            "cascader-popup",
            `dropdown-size-${dropdownSize}`
          )}
          className={value ? classNames("input-filled") : ""}
          size={CORESizeToAntSize[size]}
          suffixIcon={suffixIcon}
          showSearch={true}
          tagRender={tagRender}
          maxTagPlaceholder={(tags) => {
            if (tags.length === 0) return;
            return (
              <CORETooltip
                testID={generateTestId(
                  "admin",
                  "publication-modified-date-tooltip"
                )}
                overlay
                width={"fit-content"}
                trigger={"hover"}
                message={
                  <Row
                    gutter={[spacing.xs, spacing.xs]}
                    justify={"center"}
                    className={"max-tag-placeholder-overflow"}
                  >
                    {tags.map((tag, index) => (
                      <Col key={tag.key}>
                        <CORETag
                          type={"basic"}
                          label={tag.label}
                          onMouseDown={onPreventMouseDown}
                          closable
                          onClose={() => {
                            if (value) {
                              if (value.length === tags.length) {
                                value?.splice(index, 1);
                                onChange &&
                                  onChange(
                                    JSON.parse(JSON.stringify(value)),
                                    []
                                  );
                              } else {
                                const diffIndexTag = value.length - tags.length;
                                const valueIndex = index + diffIndexTag;
                                value?.splice(valueIndex, 1);
                                onChange &&
                                  onChange(
                                    JSON.parse(JSON.stringify(value)),
                                    []
                                  );
                              }
                            }
                          }}
                        />
                      </Col>
                    ))}
                  </Row>
                }
              >
                <span
                  onMouseDown={onPreventMouseDown}
                >{`+${tags.length}`}</span>
              </CORETooltip>
            );
          }}
          value={value}
          options={options}
          multiple={multiple}
          expandIcon={
            <COREIcon icon={icon({ name: "caret-right", style: "solid" })} />
          }
          clearIcon={
            <COREIcon icon={icon({ name: "circle-xmark", style: "solid" })} />
          }
          maxTagCount={"responsive"}
          onChange={onChange}
          {...props}
        />
      </div>
    </TestIDWrapper>
  );
};
